// Small local-time date helpers. Dates as "YYYY-MM-DD" strings are always local days.
// "Local" means the app's display zone (appZone): the device's zone, unless the user picked
// another one for this device. Zone-aware helpers (wallToMs, msToWall, appZone…) are at the end.

export const MS_MIN = 60_000;
export const MS_HOUR = 3_600_000;
export const MS_DAY = 86_400_000;

const pad = (n: number) => String(n).padStart(2, '0');

export function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayStr(): string {
  const o = displayZoneOverride();
  return o ? msToWall(Date.now(), o).slice(0, 10) : toDateStr(new Date());
}

/** Local midnight of a "YYYY-MM-DD". */
export function parseDateStr(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

export function addDays(s: string, n: number): string {
  const d = parseDateStr(s);
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}

export function addMonths(s: string, n: number): string {
  const d = parseDateStr(s);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return toDateStr(d);
}

export function diffDays(a: string, b: string): number {
  return Math.round((parseDateStr(b).getTime() - parseDateStr(a).getTime()) / MS_DAY);
}

/** weekStartsOn: 0 = Sunday, 1 = Monday */
export function startOfWeek(s: string, weekStartsOn = 1): string {
  const d = parseDateStr(s);
  const diff = (d.getDay() - weekStartsOn + 7) % 7;
  return addDays(s, -diff);
}

export function startOfMonth(s: string): string {
  return `${s.slice(0, 7)}-01`;
}

export function daysInMonth(year: number, month0: number): number {
  return new Date(year, month0 + 1, 0).getDate();
}

/** ISO-8601 week key, e.g. "2026-W43". */
export function isoWeekKey(s: string): string {
  const d = parseDateStr(s);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayNr = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const week =
    1 +
    Math.round(
      ((target.getTime() - firstThursday.getTime()) / MS_DAY -
        3 +
        ((firstThursday.getDay() + 6) % 7)) /
        7,
    );
  return `${target.getFullYear()}-W${pad(week)}`;
}

export function monthKey(s: string): string {
  return s.slice(0, 7);
}

export function quarterKey(s: string): string {
  const m = Number(s.slice(5, 7));
  return `${s.slice(0, 4)}-Q${Math.ceil(m / 3)}`;
}

export function decadeKey(s: string): string {
  return `${s.slice(0, 3)}0s`;
}

export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
export const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

/** "1m", "4m", "2h", "1d", "3w", "2y" like the notes list. */
export function relativeShort(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  if (d < 365) return `${Math.floor(d / 7)}w`;
  return `${Math.floor(d / 365)}y`;
}

/** 3723000 → "1:02:03"; under an hour "2:03". */
export function formatDuration(ms: number, alwaysHours = false): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return h || alwaysHours ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

/** 5400000 → "1h 30m" */
export function formatHours(ms: number): string {
  const totalMin = Math.round(ms / MS_MIN);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function formatTime(d: Date | number, h24 = false): string {
  return formatTimeIn(typeof d === 'number' ? d : d.getTime(), null, h24);
}

export function formatDay(
  s: string,
  opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' },
): string {
  return parseDateStr(s).toLocaleDateString([], opts);
}

/** "Today", "Tomorrow", "Yesterday", weekday within a week, else "Oct 31". */
export function friendlyDay(s: string): string {
  const diff = diffDays(todayStr(), s);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 1 && diff < 7) return parseDateStr(s).toLocaleDateString([], { weekday: 'long' });
  const sameYear = s.slice(0, 4) === todayStr().slice(0, 4);
  return parseDateStr(s).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** "YYYY-MM-DDTHH:mm" local (in the display zone) → epoch ms. */
export function localDateTimeToMs(s: string): number {
  const o = displayZoneOverride();
  if (o) return wallToMs(s, o);
  const [date, time = '00:00'] = s.split('T');
  const d = parseDateStr(date!);
  const [h, m] = time.split(':').map(Number);
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d.getTime();
}

export function msToLocalDateTime(ms: number): string {
  const o = displayZoneOverride();
  if (o) return msToWall(ms, o);
  const d = new Date(ms);
  return `${toDateStr(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function startOfDayMs(s: string): number {
  return localDateTimeToMs(`${s}T00:00`);
}

// ------------------------------------------------------------------ time zones
//
// The rules (see also CalEvent / TimeEntry in types.ts):
// - Instants (time entries, timers, flights, zoned events) are epoch ms or a wall time plus an
//   IANA zone; they are shown in the *display zone* (appZone): the device's current zone, or
//   the zone the user picked for this device (setDisplayZone). "Today", day boundaries, the
//   now-line and every "local" helper in this file follow it.
// - Dates ("YYYY-MM-DD": all-day events, due dates, habits, journal days, birthdays) are
//   calendar days and never move when the zone changes.
// - A wall time without a zone ("floating") means that clock time in the display zone.
// - New zoned things (events, time entries) record appZone(), the zone the user is looking
//   at, so they land on the day and clock time they were entered on.
// The device zone is re-read at most once a second (reading it builds an Intl formatter, slow
// on phones and needed for every time shown), so travelling just works once the screen
// re-renders (see onClockChange). Durations are always instant differences (ms).

let deviceZoneCache = { zone: '', at: 0 };

/** The device's current IANA zone, e.g. "America/Chicago". */
export function deviceZone(): string {
  const now = Date.now();
  if (deviceZoneCache.zone && Math.abs(now - deviceZoneCache.at) < 1000)
    return deviceZoneCache.zone;
  let zone = 'UTC';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {}
  deviceZoneCache = { zone, at: now };
  return zone;
}

/** For tests: forget the cached device zone (as after the device changes zone). */
export function _resetDeviceZoneCache() {
  deviceZoneCache = { zone: '', at: 0 };
}

// ------------------------------------------------------------------ display zone (per device)

const DISPLAY_KEY = 'ember.displayZone';
const DISPLAY_ACK_KEY = 'ember.displayZoneAck';
let displayOverride: string | null | undefined; // undefined = not read from storage yet

function readStored(key: string): string | null {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    const v = raw ? (JSON.parse(raw) as unknown) : null;
    return typeof v === 'string' ? v : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, v: string | null) {
  try {
    if (v === null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, JSON.stringify(v));
  } catch {}
}

/** The zone picked for this device, or null for Automatic (follow the device). */
export function displayZoneOverride(): string | null {
  if (displayOverride === undefined) {
    const v = readStored(DISPLAY_KEY);
    displayOverride = isValidZone(v) ? v : null;
  }
  return displayOverride;
}

/** The zone the app shows times and days in: the override, else the device's zone. */
export function appZone(): string {
  return displayZoneOverride() ?? deviceZone();
}

/**
 * Pick the display zone for this device (not synced: a laptop and a phone can differ).
 * null = Automatic. Remembers the device's zone at the time, so a later device change can ask.
 */
export function setDisplayZone(tz: string | null) {
  displayOverride = isValidZone(tz) ? tz : null;
  writeStored(DISPLAY_KEY, displayOverride);
  writeStored(DISPLAY_ACK_KEY, displayOverride ? deviceZone() : null);
  checkClock();
}

/** The device zone the user last saw or answered for while an override was on. */
export function displayZoneAck(): string | null {
  return readStored(DISPLAY_ACK_KEY);
}

/** "Keep Berlin": don't ask again until the device moves to yet another zone. */
export function ackDeviceZone(device = deviceZone()) {
  writeStored(DISPLAY_ACK_KEY, device);
  checkClock();
}

/**
 * Should the app offer to follow the device? Only with an override on, when the device has moved
 * to a zone the user hasn't answered for yet and whose clock differs from the override's.
 * With Automatic there's nothing to ask: the app just follows.
 */
export function zoneSwitchPrompt(
  override: string | null,
  device: string,
  ack: string | null,
  ms = Date.now(),
): { from: string; to: string } | null {
  if (!override || !isValidZone(override)) return null;
  if (device === override || device === ack) return null;
  if (!zonesDiffer(device, override, ms)) return null;
  return { from: override, to: device };
}

/** For tests: forget the cached override (as after a reload). */
export function _resetDisplayZoneCache() {
  displayOverride = undefined;
}

const zoneFmt = new Map<string, Intl.DateTimeFormat | null>();
function zoneFormatter(tz: string): Intl.DateTimeFormat | null {
  if (zoneFmt.has(tz)) return zoneFmt.get(tz)!;
  let f: Intl.DateTimeFormat | null = null;
  try {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    f = null;
  }
  zoneFmt.set(tz, f);
  return f;
}

export function isValidZone(tz: string | null | undefined): tz is string {
  return !!tz && zoneFormatter(tz) !== null;
}

function zoneParts(ms: number, tz: string) {
  const p: Record<string, number> = {};
  for (const part of zoneFormatter(tz)!.formatToParts(new Date(ms)))
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  return {
    y: p.year!,
    mo: p.month!,
    d: p.day!,
    h: p.hour === 24 ? 0 : p.hour!,
    mi: p.minute!,
    s: p.second!,
  };
}

/** Offset of a zone at an instant (wall clock there minus UTC), in ms. */
export function zoneOffsetMs(tz: string | null | undefined, ms: number): number {
  if (!isValidZone(tz)) {
    const o = displayZoneOverride();
    if (o) return zoneOffsetMs(o, ms);
    return -new Date(ms).getTimezoneOffset() * MS_MIN;
  }
  const p = zoneParts(ms, tz);
  const asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  return asUtc - (ms - (((ms % 1000) + 1000) % 1000));
}

/**
 * "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm" read as a wall-clock time in `tz` → epoch ms.
 * No zone (or an unknown one) means the display zone. A time skipped by a DST jump moves forward
 * (like Date does); a time that happens twice picks the first.
 */
export function wallToMs(s: string, tz?: string | null): number {
  if (!isValidZone(tz)) return localDateTimeToMs(s);
  const [date, time = '00:00'] = s.split('T');
  const [y, mo, d] = date!.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const guess = Date.UTC(y!, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  // The offset either side of the wall time covers a DST change on that day.
  const offs = new Set([zoneOffsetMs(tz, guess - MS_DAY), zoneOffsetMs(tz, guess + MS_DAY)]);
  const cands = [...offs].map((o) => guess - o).sort((a, b) => a - b);
  const want = `${date}T${pad(h ?? 0)}:${pad(mi ?? 0)}`;
  const exact = cands.find((t) => msToWall(t, tz) === want);
  return exact ?? cands[cands.length - 1]!;
}

/** Epoch ms → "YYYY-MM-DDTHH:mm" on the wall clock of `tz` (display zone without one). */
export function msToWall(ms: number, tz?: string | null): string {
  if (!isValidZone(tz)) return msToLocalDateTime(ms);
  const p = zoneParts(ms, tz);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

/** The calendar day of an instant in `tz` (display zone without one). */
export function dayInZone(ms: number, tz?: string | null): string {
  return msToWall(ms, tz).slice(0, 10);
}

/** Do two zones show different clocks at this instant? (Chicago and Winnipeg don't.) */
export function zonesDiffer(
  a: string | null | undefined,
  b: string | null | undefined,
  ms: number,
) {
  return zoneOffsetMs(a, ms) !== zoneOffsetMs(b, ms);
}

/** "London", "New York", "UTC" from an IANA name. */
let zoneAlias: { tz: string; name: string } | null = null;

/** Call one zone by a nearer city ("America/New_York" → "Fort Myers" when that's home). */
export function setZoneName(tz: string | null | undefined, name: string | null | undefined) {
  zoneAlias = tz && name ? { tz, name } : null;
}

export function zoneCity(tz: string): string {
  if (zoneAlias && zoneAlias.tz === tz) return zoneAlias.name;
  const last = tz.split('/').pop() ?? tz;
  return last.replace(/_/g, ' ');
}

/** Short zone name at an instant: "CDT", "BST" or "GMT+9". */
export function zoneAbbr(tz: string | null | undefined, ms: number): string {
  try {
    const p = new Intl.DateTimeFormat([], {
      timeZone: isValidZone(tz) ? tz : (displayZoneOverride() ?? undefined),
      timeZoneName: 'short',
    }).formatToParts(new Date(ms));
    return p.find((x) => x.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Clock time of an instant in a given zone ("9:00 AM"). */
export function formatTimeIn(ms: number, tz?: string | null, h24 = false): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: !h24,
    timeZone: isValidZone(tz) ? tz : (displayZoneOverride() ?? undefined),
  });
}

/** "GMT+2", "GMT-3:30", "GMT" for a zone at an instant (display zone without one). */
export function formatOffset(tz: string | null | undefined, ms: number): string {
  const min = Math.round(zoneOffsetMs(tz, ms) / MS_MIN);
  if (!min) return 'GMT';
  const a = Math.abs(min);
  return `GMT${min < 0 ? '-' : '+'}${Math.floor(a / 60)}${a % 60 ? `:${pad(a % 60)}` : ''}`;
}

/** Every IANA zone the runtime knows (for pickers), with the device zone guaranteed in. */
export function allZones(): string[] {
  let zones: string[] = [];
  try {
    zones =
      (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.(
        'timeZone',
      ) ?? [];
  } catch {}
  const here = deviceZone();
  if (!zones.includes(here)) zones = [here, ...zones];
  if (!zones.includes('UTC')) zones.push('UTC');
  return zones;
}

// ------------------------------------------------------------------ "today" while open

/** Changes when the local day, the device's zone/offset or the display zone changes. */
export function clockKey(): string {
  const o = displayZoneOverride();
  const pick = o ? `|${o}|${displayZoneAck() ?? ''}` : '';
  return `${todayStr()}|${deviceZone()}|${new Date().getTimezoneOffset()}${pick}`;
}

const clockListeners = new Set<() => void>();
let clockTimer: ReturnType<typeof setInterval> | null = null;
let lastClock = '';

/** Another window of the app on this device changed the display zone. */
function onStorage(e: StorageEvent) {
  if (e.key === DISPLAY_KEY || e.key === DISPLAY_ACK_KEY || e.key === null) {
    displayOverride = undefined;
    checkClock();
  }
}

function checkClock() {
  const k = clockKey();
  if (k === lastClock) return;
  lastClock = k;
  for (const l of clockListeners) l();
}

/**
 * Called when midnight passes or the zone changes (landing somewhere, DST) while the app is
 * open. Checked every 20 s and whenever the app comes back to the foreground.
 */
export function onClockChange(fn: () => void): () => void {
  clockListeners.add(fn);
  if (!clockTimer && typeof window !== 'undefined') {
    lastClock = clockKey();
    clockTimer = setInterval(checkClock, 20_000);
    window.addEventListener('focus', checkClock);
    document.addEventListener('visibilitychange', checkClock);
    window.addEventListener('storage', onStorage);
  }
  return () => {
    clockListeners.delete(fn);
    if (!clockListeners.size && clockTimer) {
      clearInterval(clockTimer);
      clockTimer = null;
      window.removeEventListener('focus', checkClock);
      document.removeEventListener('visibilitychange', checkClock);
      window.removeEventListener('storage', onStorage);
    }
  };
}
